<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

import Avatar from 'next/avatar/Avatar.vue';
import EmojiGlyph from 'shared/components/emoji/EmojiGlyph.vue';
import WhatsAppEmojiPicker from 'dashboard/components/widgets/conversation/WhatsAppEmojiPicker.vue';
import { useMapGetter } from 'dashboard/composables/store';

// Aceleres: detalhe das reacoes no padrao WhatsApp. O card abre ANCORADO na
// chip de reacao da mensagem (nao centralizado), lista quem reagiu e permite
// remover ou trocar a propria reacao. A API de reacao ja e um toggle por
// usuario (mesmo emoji remove, emoji diferente substitui), entao tudo aqui
// reaproveita o mesmo endpoint, sem rota nova.
const props = defineProps({
  channelType: {
    type: String,
    default: '',
  },
  reactions: {
    type: Array,
    default: () => [],
  },
  currentUserId: {
    type: Number,
    default: null,
  },
  // Elemento da chip que abriu o card. Guardamos o ELEMENTO, e nao um
  // retangulo ja medido: o card e position:fixed, portanto vive em
  // coordenadas de viewport, e uma medicao feita no clique deixa de valer
  // assim que a lista rola ou o layout mexe (uma imagem a acabar de
  // carregar chega). Medindo a partir do elemento, o card acompanha a chip.
  anchorEl: {
    type: Object,
    default: null,
  },
  // 'right' = mensagem enviada (bolha a direita). Decide para que lado o
  // card cresce a partir do clique.
  orientation: {
    type: String,
    default: 'left',
  },
});

const emit = defineEmits(['close', 'react']);

const agents = useMapGetter('agents/getAgents');
const selectedChat = useMapGetter('getSelectedChat');

const showPicker = ref(false);
const pickerLabel = 'Escolher reação';

const CARD_WIDTH = 320;
const CARD_MAX_HEIGHT = 340;
const GAP = 6;
const EDGE = 8;

// A MessageList converte content_attributes para camelCase (deep), entao as
// reacoes chegam como `userId`/`fromContact`. Ler so a forma snake deixava
// toda reacao "de outra pessoa": sumia o "Você" e a opcao de remover.
const reactionUserId = reaction => reaction?.user_id ?? reaction?.userId;
const isFromContact = reaction =>
  Boolean(reaction?.from_contact ?? reaction?.fromContact);

const isOwnReaction = reaction =>
  !isFromContact(reaction) &&
  props.currentUserId != null &&
  reactionUserId(reaction) != null &&
  Number(reactionUserId(reaction)) === Number(props.currentUserId);

const ownEmoji = computed(
  () => props.reactions.find(isOwnReaction)?.emoji || null
);
const isInstagramChannel = computed(
  () => props.channelType === 'Channel::Instagram'
);
const isMessengerChannel = computed(
  () => props.channelType === 'Channel::FacebookPage'
);

const distinctEmojis = list => {
  const seen = [];
  list.forEach(reaction => {
    const { emoji } = reaction || {};
    if (emoji && !seen.includes(emoji)) seen.push(emoji);
  });
  return seen;
};

const chips = computed(() =>
  distinctEmojis(props.reactions).map(emoji => ({
    emoji,
    count: props.reactions.filter(reaction => reaction?.emoji === emoji).length,
  }))
);

const total = computed(() => props.reactions.length);

const title = computed(() =>
  total.value === 1 ? '1 reação' : `${total.value} reações`
);

const contactSender = computed(() => selectedChat.value?.meta?.sender || {});

const avatarFor = reaction => {
  if (isFromContact(reaction)) return contactSender.value?.thumbnail || '';
  const agent = (agents.value || []).find(
    item => Number(item.id) === Number(reactionUserId(reaction))
  );
  return agent?.thumbnail || '';
};

const nameFor = reaction =>
  isOwnReaction(reaction) ? 'Você' : reaction?.name || 'Contato';

// "Você" primeiro, como no WhatsApp; o resto mantem a ordem de chegada.
const visibleReactions = computed(() =>
  [...props.reactions].sort((first, second) => {
    if (isOwnReaction(first) === isOwnReaction(second)) return 0;
    return isOwnReaction(first) ? -1 : 1;
  })
);

// Só a chip da propria reacao responde ao clique — a de outra pessoa nao muda
// nada, igual ao WhatsApp.
const isMyChip = emoji => emoji === ownEmoji.value;

const handleChipClick = emoji => {
  if (isMessengerChannel.value) return;
  if (!isMyChip(emoji)) return;
  emit('react', emoji);
};

const handleRowClick = reaction => {
  if (isMessengerChannel.value) return;
  if (!isOwnReaction(reaction)) return;
  emit('react', reaction.emoji);
};

const handlePick = emoji => {
  if (isMessengerChannel.value) return;
  showPicker.value = false;
  emit('react', emoji);
};

// Medicao viva da chip. Atualizada ao montar (e nao no clique: entre o
// clique e a pintura o layout ainda pode mexer) e a cada rolagem ou
// redimensionamento. O `true` no listener de scroll e o que importa — a
// conversa rola num contentor interno, e sem a fase de captura o evento
// nunca chegaria ao document.
const anchorRect = ref(null);
const cardEl = ref(null);
const cardRect = ref(null);
let resizeObserver;

const syncAnchorRect = () => {
  const el = props.anchorEl;
  if (!el?.getBoundingClientRect) return;

  const rect = el.getBoundingClientRect();
  // Elemento retirado do DOM devolve tudo a zero. Nesse caso mantém-se a
  // última medição boa: com o retângulo zerado o card seria arrastado para
  // o canto superior esquerdo, longe de onde o utilizador clicou.
  if (rect.width === 0 && rect.height === 0) return;

  anchorRect.value = rect;
};

const syncCardRect = () => {
  const rect = cardEl.value?.getBoundingClientRect();
  if (!rect) return;

  cardRect.value = { width: rect.width, height: rect.height };
};

// Posiciona o card junto da reacao, escolhe o lado com mais espaco e limita-o
// ao tamanho disponivel na janela.
const cardStyle = computed(() => {
  const anchor = anchorRect.value;
  const viewportWidth = window.innerWidth || CARD_WIDTH;
  const viewportHeight = window.innerHeight || CARD_MAX_HEIGHT;
  const width = Math.min(CARD_WIDTH, Math.max(0, viewportWidth - EDGE * 2));

  if (!anchor) {
    return { left: `${EDGE}px`, top: `${EDGE}px`, width: `${width}px` };
  }

  const leftLimit = Math.max(EDGE, viewportWidth - width - EDGE);
  const alignedLeft =
    props.orientation === 'right' ? anchor.right - width : anchor.left;
  const left = Math.min(leftLimit, Math.max(EDGE, alignedLeft));

  const spaceBelow = Math.max(0, viewportHeight - anchor.bottom - GAP - EDGE);
  const spaceAbove = Math.max(0, anchor.top - GAP - EDGE);
  const openDown = spaceBelow >= spaceAbove;
  const availableHeight = Math.max(spaceBelow, spaceAbove);
  const maxHeight = Math.min(CARD_MAX_HEIGHT, availableHeight);
  const height = Math.min(cardRect.value?.height || maxHeight, maxHeight);
  const idealTop = openDown ? anchor.bottom + GAP : anchor.top - GAP - height;
  const topLimit = Math.max(EDGE, viewportHeight - height - EDGE);
  const top = Math.min(topLimit, Math.max(EDGE, idealTop));

  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
    width: `${Math.round(width)}px`,
    maxHeight: `${Math.max(0, Math.round(maxHeight))}px`,
  };
});

const handleKeydown = event => {
  if (event.key !== 'Escape') return;
  event.preventDefault();
  if (showPicker.value) {
    showPicker.value = false;
    return;
  }
  emit('close');
};

onMounted(async () => {
  syncAnchorRect();
  await nextTick();
  syncCardRect();

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(syncCardRect);
    if (cardEl.value) resizeObserver.observe(cardEl.value);
    if (props.anchorEl) resizeObserver.observe(props.anchorEl);
  }

  document.addEventListener('keydown', handleKeydown);
  // Captura (`true`) para apanhar tambem a rolagem do contentor da conversa,
  // que nao borbulha ate ao document.
  window.addEventListener('scroll', syncAnchorRect, true);
  window.addEventListener('resize', syncAnchorRect);
});
onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  document.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('scroll', syncAnchorRect, true);
  window.removeEventListener('resize', syncAnchorRect);
});
</script>

<template>
  <div
    class="aceleres-reactions-modal fixed inset-0 z-[9999]"
    @click.self="emit('close')"
  >
    <div
      ref="cardEl"
      class="aceleres-reactions-modal__card fixed flex flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-[0_7px_12px_-9px_rgba(0,0,0,0.48)]"
      :class="{ invisible: props.anchorEl && !anchorRect }"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      :style="cardStyle"
      @click.stop
    >
      <div class="px-3 pb-1.5 pt-2.5 text-sm font-semibold text-n-slate-12">
        {{ title }}
      </div>

      <div
        class="flex items-center gap-1 overflow-x-auto border-b border-n-weak px-3 pb-2"
      >
        <button
          v-if="!isInstagramChannel && !isMessengerChannel"
          type="button"
          :aria-label="pickerLabel"
          class="inline-flex h-8 w-9 flex-shrink-0 items-center justify-center rounded-full border border-n-weak text-n-slate-11 transition-colors hover:bg-n-alpha-1"
          :class="{ 'bg-n-alpha-1': showPicker }"
          @click.stop="showPicker = true"
        >
          <span class="i-lucide-smile-plus h-5 w-5" />
        </button>
        <button
          v-else-if="isInstagramChannel"
          type="button"
          aria-label="Reagir com coração"
          :disabled="ownEmoji === '❤️'"
          class="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-n-weak text-2xl leading-none text-n-slate-11 transition-colors enabled:hover:bg-n-alpha-1 disabled:cursor-default disabled:opacity-60"
          @click.stop="handlePick('❤️')"
        >
          <span aria-hidden="true">♥︎</span>
        </button>

        <button
          v-for="chip in chips"
          :key="chip.emoji"
          type="button"
          class="inline-flex h-8 flex-shrink-0 items-center gap-1 rounded-full px-2 text-xs transition-colors"
          :class="
            !isMessengerChannel && isMyChip(chip.emoji)
              ? 'cursor-pointer bg-n-alpha-2 font-medium text-n-slate-12'
              : 'cursor-default text-n-slate-11'
          "
          @click.stop="handleChipClick(chip.emoji)"
        >
          <EmojiGlyph
            :emoji="chip.emoji"
            size="reaction"
            class="!h-4 !w-4 !text-base"
          />
          <span>{{ chip.count }}</span>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto py-1">
        <button
          v-for="(reaction, index) in visibleReactions"
          :key="`${reaction.emoji}-${reaction.user_id}-${index}`"
          type="button"
          class="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors"
          :class="
            !isMessengerChannel && isOwnReaction(reaction)
              ? 'cursor-pointer hover:bg-n-alpha-1'
              : 'cursor-default'
          "
          @click.stop="handleRowClick(reaction)"
        >
          <Avatar
            :name="nameFor(reaction)"
            :src="avatarFor(reaction)"
            :size="28"
            rounded-full
          />
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm text-n-slate-12">
              {{ nameFor(reaction) }}
            </div>
          </div>
          <EmojiGlyph
            :emoji="reaction.emoji"
            size="reaction"
            class="flex-shrink-0 !h-5 !w-5 !text-xl"
          />
        </button>
      </div>
    </div>

    <div
      v-if="showPicker && !isInstagramChannel"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4"
      @click.self="showPicker = false"
    >
      <WhatsAppEmojiPicker
        class="animate-[fade-in-up_0.16s_ease-out_both]"
        embedded
        show-recent-reactions
        :on-select="handlePick"
        @click.stop
      />
    </div>
  </div>
</template>
